import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "../../hooks/usePageMeta";
import { ARTICLES } from "../../mocks/articles";
import { CATEGORIES, getCategory } from "../../mocks/site";
import { formatCount } from "../../utils/format";
import Breadcrumb from "../../components/feature/Breadcrumb";

function NewsRow({ id, title, cover, coverAlt, category, summary, author, date, views, tags }: (typeof ARTICLES)[number]) {
  const cat = getCategory(category);
  return (
    <article className="group flex flex-col gap-5 overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-200 sm:flex-row md:p-5">
      <Link to={`/news/${id}`} className="block w-full shrink-0 overflow-hidden rounded-xl sm:w-52 md:w-64" aria-label={title}>
        <img src={cover} alt={coverAlt} className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:aspect-[4/3]" />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {cat && (
            <Link to={`/news?cat=${category}`} className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700 hover:bg-primary-200">
              <i className={cat.icon} aria-hidden="true"></i>{cat.name}
            </Link>
          )}
          {tags.slice(0, 2).map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-1 text-foreground-600">{t}</span>
          ))}
        </div>
        <Link to={`/news/${id}`}>
          <h2 className="mt-3 line-clamp-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
            {title}
          </h2>
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{summary}</p>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1"><i className="ri-user-3-line" aria-hidden="true"></i>{author}</span>
          <span className="flex items-center gap-1"><i className="ri-time-line" aria-hidden="true"></i>{date}</span>
          <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{formatCount(views)} 阅读</span>
          <span className="flex items-center gap-1 text-primary-600"><i className="ri-fire-line" aria-hidden="true"></i>热度 高</span>
        </div>
      </div>
    </article>
  );
}

export default function News() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "all";
  const query = (params.get("q") || "").trim().toLowerCase();
  const [keyword, setKeyword] = useState(query);

  usePageMeta({
    title: "资讯速递 · 最新娱乐新瓜与热门动态",
    description:
      "吃瓜17资讯速递页，按影视、综艺、音乐、明星动态与热点话题分类汇总最新娱乐内容，支持关键词搜索，助你快速找到感兴趣的新鲜猛料。",
    keywords: "吃瓜17,娱乐资讯,影视速递,综艺现场,明星动态,热点话题,最新爆料",
    path: "/news",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "吃瓜17 资讯速递",
        url: "https://xttkax.cn/news",
        inLanguage: "zh-CN",
        mainEntity: { "@type": "ItemList", numberOfItems: ARTICLES.length, itemListElement: ARTICLES.slice(0, 8).map((a, i) => ({ "@type": "ListItem", position: i + 1, name: a.title, url: `https://xttkax.cn/news/${a.id}` })) },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [{ "@type": "ListItem", position: 1, name: "今日首页", item: "https://xttkax.cn/" }, { "@type": "ListItem", position: 2, name: "资讯速递" }],
      },
    ],
  });

  const list = useMemo(() => {
    let arr = ARTICLES;
    if (activeCat !== "all") arr = arr.filter((a) => a.category === activeCat);
    if (query) arr = arr.filter((a) => (a.title + a.summary + a.tags.join("")).toLowerCase().includes(query));
    return arr;
  }, [activeCat, query]);

  const activeLabel = activeCat === "all" ? "全部速递" : getCategory(activeCat)?.name;

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setParams(next);
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="bg-primary-100/60 py-8 md:py-12">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <Breadcrumb items={[{ label: "今日首页", to: "/" }, { label: "资讯速递" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">资讯速递</h1>
          <p className="mt-2 max-w-2xl text-foreground-700">按栏目筛选最新娱乐动态，或输入关键词快速检索你关心的新瓜。</p>

          <form onSubmit={submitSearch} className="mt-6 flex w-full flex-col gap-3 sm:max-w-xl sm:flex-row">
            <label htmlFor="news-search" className="sr-only">搜索资讯</label>
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2.5">
              <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
              <input
                id="news-search"
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、综艺、明星热点…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600">
              <i className="ri-search-line" aria-hidden="true"></i>搜索
            </button>
          </form>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { const n = new URLSearchParams(params); n.delete("cat"); n.delete("q"); setKeyword(""); setParams(n); }}
              className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${activeCat === "all" ? "bg-foreground-950 font-semibold text-background-50" : "bg-background-50 text-foreground-700 hover:bg-secondary-200"}`}
            >
              全部速递
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => { const n = new URLSearchParams(params); n.set("cat", c.slug); n.delete("q"); setKeyword(""); setParams(n); }}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${activeCat === c.slug ? "bg-foreground-950 font-semibold text-background-50" : "bg-background-50 text-foreground-700 hover:bg-secondary-200"}`}
              >
                <i className={c.icon} aria-hidden="true"></i>{c.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            {query ? <>关键词“<strong className="text-primary-700">{query}</strong>”的结果</> : <>{activeLabel}</>} · 共 <strong>{list.length}</strong> 条
          </p>
        </div>
        {list.length ? (
          <div className="flex flex-col gap-5">
            {list.map((a) => <NewsRow key={a.id} {...a} />)}
          </div>
        ) : (
          <div className="rounded-2xl border border-background-200/70 bg-background-50 py-20 text-center">
            <i className="ri-inbox-2-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-foreground-600">暂未找到相关内容，换个关键词或栏目试试吧。</p>
            <button
              type="button"
              onClick={() => { setParams(new URLSearchParams()); setKeyword(""); }}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>查看全部
            </button>
          </div>
        )}
      </div>
    </div>
  );
}